import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound } from "lucide-react";
import { api } from "../api";
import { PasswordInput } from "../components/PasswordInput";
import { AuthLayout } from "../components/AuthLayout";

function scorePassword(pw: string): { label: string; pct: number; tone: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const map: Record<number, { label: string; pct: number; tone: string }> = {
    0: { label: "Too short", pct: 5, tone: "bg-rose-500" },
    1: { label: "Weak", pct: 20, tone: "bg-rose-500" },
    2: { label: "Fair", pct: 45, tone: "bg-amber-500" },
    3: { label: "Good", pct: 70, tone: "bg-lime-500" },
    4: { label: "Strong", pct: 90, tone: "bg-emerald-500" },
    5: { label: "Very strong", pct: 100, tone: "bg-emerald-600" },
  };
  return map[score];
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = scorePassword(password);
  const passwordError =
    password.length === 0 ? null
    : password.length < 8 ? "Use at least 8 characters."
    : null;
  const confirmError =
    confirm.length === 0 ? null
    : confirm !== password ? "Passwords don't match."
    : null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("The reset link is missing its token. Request a new one.");
      return;
    }
    if (passwordError || confirmError || !password) {
      setError(passwordError || confirmError || "Fill both password fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword(token, password);
      setDone(true);
      window.setTimeout(() => navigate("/login?reset=1"), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="Missing reset token"
        subtitle="This URL doesn't include a reset token."
      >
        <div className="card">
          <div className="card-body text-center space-y-4">
            <p className="text-sm text-slate-600">Request a fresh reset link to continue.</p>
            <Link to="/forgot-password" className="btn-primary w-full">Get a reset link</Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={done ? "Password updated" : "Set a new password"}
      subtitle={done ? "Redirecting you to sign in…" : "Pick something you haven't used elsewhere."}
    >
      {done ? (
        <div className="card">
          <div className="card-body text-center space-y-3">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <p className="text-sm text-ink">Password updated. Redirecting…</p>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="card" noValidate>
          <div className="card-body space-y-4">
            <PasswordInput
              id="new-pw"
              label="New password"
              value={password}
              onChange={setPassword}
              autoFocus
              minLength={8}
              autoComplete="new-password"
              disabled={submitting}
              error={passwordError}
              helpText="At least 8 characters. Longer is better."
            />
            {password.length > 0 && (
              <div>
                <div className="h-1.5 rounded bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${strength.tone} transition-all`}
                    style={{ width: `${strength.pct}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">Strength: {strength.label}</p>
              </div>
            )}
            <PasswordInput
              id="confirm-pw"
              label="Confirm new password"
              value={confirm}
              onChange={setConfirm}
              minLength={8}
              autoComplete="new-password"
              disabled={submitting}
              error={confirmError}
            />
            {error && (
              <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={submitting || !password || !!passwordError || !!confirmError || password !== confirm}
            >
              {submitting ? (
                <><span className="spinner" aria-hidden="true" /> Updating…</>
              ) : (
                <><KeyRound className="h-4 w-4" aria-hidden="true" /> Update password</>
              )}
            </button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-brand-600 hover:text-brand-700 hover:underline">
                Back to sign in
              </Link>
            </div>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
